import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  UsersThree, ArrowLeft, MapPin, GameController, Clock, Eye, Star
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const GROUP_TYPES = [
  { value: 'casual', label: 'Casual — relaxed social play' },
  { value: 'competitive', label: 'Competitive — serious games' },
  { value: 'shop_community', label: 'Shop Community — hosted by a game shop' },
  { value: 'league_group', label: 'League Group — paired with a league' },
  { value: 'private_group', label: 'Private Group — invite only' },
  { value: 'public_group', label: 'Public Group — open to all' },
];

const VISIBILITIES = [
  { value: 'public', label: 'Public — anyone can find and join' },
  { value: 'unlisted', label: 'Unlisted — join via link only' },
  { value: 'private', label: 'Private — invite required' },
];

const VENUE_TYPES = [
  { value: 'shop', label: 'Game Shop' },
  { value: 'community_center', label: 'Community Center' },
  { value: 'private', label: 'Private Location' },
  { value: 'online', label: 'Online' },
  { value: 'other', label: 'Other' },
];

const COMP_LEVELS = [
  { value: 'casual', label: 'Casual' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'competitive', label: 'Competitive' },
];

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Monthly' },
];

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function CreateRecurringGroupPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', visibility: 'public', group_type: 'casual',
    primary_game_id: '', city: '', state: '', venue_type: 'shop', venue_name: '',
    beginner_friendly: true, competitive_level: 'casual', max_members: 50,
    recurring_frequency: 'weekly', recurring_day: 4, recurring_time: '19:00', recurring_duration: 180,
  });

  const set = (k, v) => setForm({ ...form, [k]: v });
  const inputCls = "w-full bg-[#0c0c14] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[13px] text-white focus:border-[#9D4CDD]/40 focus:outline-none placeholder-[#2a2a2a]";
  const labelCls = "text-[11px] text-[#555] uppercase tracking-wider font-medium mb-1.5 block";
  const selectCls = inputCls;

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Group name is required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name, description: form.description, visibility: form.visibility,
        group_type: form.group_type, primary_game_id: form.primary_game_id || null,
        city: form.city || null, state: form.state || null,
        venue_type: form.venue_type, venue_name: form.venue_name || null,
        beginner_friendly: form.beginner_friendly, competitive_level: form.competitive_level,
        max_members: form.max_members,
        recurring_rule: {
          frequency: form.recurring_frequency,
          day_of_week: parseInt(form.recurring_day),
          time: form.recurring_time,
          duration_minutes: parseInt(form.recurring_duration),
        },
      };
      const res = await axios.post(`${API}/api/recurring-groups`, payload, { withCredentials: true });
      toast.success('Group created!');
      navigate(`/groups/recurring`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create group');
    }
    setSubmitting(false);
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="create-group-page">
      <button onClick={() => navigate('/groups/recurring')} className="flex items-center gap-1 text-[12px] text-[#555] hover:text-white mb-4 transition-colors">
        <ArrowLeft size={14} /> Back to Groups
      </button>

      <div className="mb-5">
        <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Create Recurring Group</h1>
        <p className="text-[13px] text-[#444] mt-0.5">Start a group that meets regularly</p>
      </div>

      <div className="space-y-5">
        {/* Basic Info */}
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5">
          <h2 className="text-[14px] font-bold text-white mb-4 flex items-center gap-2">
            <UsersThree size={16} weight="duotone" className="text-[#9D4CDD]" /> Basic Info
          </h2>
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Group Name *</label>
              <input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="e.g., Portland Thursday Catan Club" data-testid="input-name" />
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea className={inputCls + " h-20 resize-none"} value={form.description}
                onChange={e => set('description', e.target.value)}
                placeholder="Tell people what your group is about..." data-testid="input-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Group Type</label>
                <select className={selectCls} value={form.group_type} onChange={e => set('group_type', e.target.value)} data-testid="select-type">
                  {GROUP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Visibility</label>
                <select className={selectCls} value={form.visibility} onChange={e => set('visibility', e.target.value)} data-testid="select-visibility">
                  {VISIBILITIES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Primary Game (optional)</label>
                <input className={inputCls} value={form.primary_game_id} onChange={e => set('primary_game_id', e.target.value)}
                  placeholder="e.g., Catan, Chess..." data-testid="input-game" />
              </div>
              <div>
                <label className={labelCls}>Max Members</label>
                <input type="number" className={inputCls} value={form.max_members} onChange={e => set('max_members', parseInt(e.target.value) || 50)}
                  data-testid="input-max-members" />
              </div>
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5">
          <h2 className="text-[14px] font-bold text-white mb-4 flex items-center gap-2">
            <MapPin size={16} weight="duotone" className="text-[#10B981]" /> Location
          </h2>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>City</label>
                <input className={inputCls} value={form.city} onChange={e => set('city', e.target.value)}
                  placeholder="Portland" data-testid="input-city" />
              </div>
              <div>
                <label className={labelCls}>State</label>
                <input className={inputCls} value={form.state} onChange={e => set('state', e.target.value)}
                  placeholder="OR" data-testid="input-state" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Venue Type</label>
                <select className={selectCls} value={form.venue_type} onChange={e => set('venue_type', e.target.value)} data-testid="select-venue">
                  {VENUE_TYPES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Venue Name</label>
                <input className={inputCls} value={form.venue_name} onChange={e => set('venue_name', e.target.value)}
                  placeholder="e.g., Board Game Cafe" data-testid="input-venue-name" />
              </div>
            </div>
          </div>
        </div>

        {/* Schedule */}
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5">
          <h2 className="text-[14px] font-bold text-white mb-4 flex items-center gap-2">
            <Clock size={16} weight="duotone" className="text-[#F59E0B]" /> Schedule
          </h2>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Frequency</label>
                <select className={selectCls} value={form.recurring_frequency} onChange={e => set('recurring_frequency', e.target.value)} data-testid="select-frequency">
                  {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Day of Week</label>
                <select className={selectCls} value={form.recurring_day} onChange={e => set('recurring_day', e.target.value)} data-testid="select-day">
                  {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Start Time</label>
                <input type="time" className={inputCls} value={form.recurring_time} onChange={e => set('recurring_time', e.target.value)} data-testid="input-time" />
              </div>
              <div>
                <label className={labelCls}>Duration (minutes)</label>
                <input type="number" className={inputCls} value={form.recurring_duration} onChange={e => set('recurring_duration', e.target.value)} data-testid="input-duration" />
              </div>
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5">
          <h2 className="text-[14px] font-bold text-white mb-4 flex items-center gap-2">
            <Star size={16} weight="duotone" className="text-[#EC4899]" /> Options
          </h2>
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Competitive Level</label>
              <select className={selectCls} value={form.competitive_level} onChange={e => set('competitive_level', e.target.value)} data-testid="select-comp-level">
                {COMP_LEVELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.beginner_friendly} onChange={e => set('beginner_friendly', e.target.checked)}
                className="w-4 h-4 rounded border-white/[0.2] bg-[#07070c]" data-testid="check-beginner" />
              <span className="text-[13px] text-white">Beginner Friendly</span>
            </label>
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/groups/recurring')}
            className="flex-1 h-10 bg-transparent border-white/[0.08] text-[#888] hover:text-white">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting}
            className="flex-1 h-10 bg-[#9D4CDD] hover:bg-[#8B3DC9] text-white font-semibold"
            data-testid="submit-group-btn">
            {submitting ? 'Creating...' : 'Create Group'}
          </Button>
        </div>
      </div>
    </div>
  );
}
